"use client"

import React, { useEffect, useState } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { i18nDefaultLocale, TLanguage } from "@/i18Config"
import { Slot } from "@radix-ui/react-slot"

import { SearchItem, searchItems } from "@/lib/azureSearchClient"
import {
  CardSearch,
  TCardSearchProps,
} from "@/components/ui/molecules/cards/card-search/CardSearch"
import { AnalyticsPageDisplay } from "@/components/templates/analytics-page-display/AnalyticsPageDisplay"
import { Pagination, TPagination } from "@/components/ui/molecules"

export interface SearchProps {
  asChild?: boolean
  className?: string
  componentTitle?: string
  cardSearch?: Array<TCardSearchProps>
  search: string
  lang?: TLanguage
}

const Search = React.forwardRef<HTMLDivElement, SearchProps>(
  (
    {
      asChild = false,
      componentTitle,
      className,
      cardSearch,
      search,
      lang: langProp,
      ...props
    },
    ref
  ) => {
    const Component = asChild ? Slot : "div"
    const [totalResultCount, setTotalResultCount] = useState<number>(0)
    const limitResults = 10
    const showPagination = totalResultCount > limitResults
    const searchParams = useSearchParams()
    const currentPageParam = searchParams.get("page")
    const searchQuery = searchParams.get("search") || ""

    // TODO: langProp is currently ignored; the site is single-locale for now.
    const lang = i18nDefaultLocale

    const pageUrl = usePathname()

    const currentPage = currentPageParam ? parseInt(currentPageParam, 10) : 1

    const [results, setResults] = useState<SearchItem[]>([])
    const [loading, setLoading] = useState<boolean>(false)

    useEffect(() => {
      setLoading(true)
      let searchResults: SearchItem[] = []

      async function fetchResultsForPage(
        input: string,
        lang: TLanguage,
        page: number
      ) {
        try {
          const searchQuery = await searchItems(input, lang, page)

          for await (const result of searchQuery.results) {
            searchResults.push(result.document)
          }
          setResults(searchResults)
          setTotalResultCount(searchQuery.count ?? 0)
        } catch (error) {
          if (error instanceof Error) {
            console.error("Error fetching results:", error.message)
          }
        }
        setLoading(false)
      }

      fetchResultsForPage(searchQuery, lang, currentPage)
    }, [currentPage, searchQuery, lang, searchParams])

    // Pagination logic
    let pageCount = Math.ceil(totalResultCount / limitResults)
    const paginationList: TPagination[] = []

    for (let i = 1; i <= pageCount; i++) {
      const url = `${pageUrl}?search=${searchQuery}&page=${i}`
      paginationList.push({
        label: i,
        isCurrent: i === currentPage,
        url,
      })
    }

    if (loading) {
      return
    }

    return (
      <>
        <AnalyticsPageDisplay
          iseKeyword={searchQuery}
          iseEngine="autocomplete"
          isePage={currentPage}
        />
        <Component
          className="mb-17 mt-10 bg-neutral-200 lg:mt-12"
          ref={ref}
          {...props}
        >
          {totalResultCount == 1 ? (
            <div className="heading--s container mb-12 text-neutral-400">
              {totalResultCount} résultat correspond à votre recherche
              {` "${searchQuery}"`}
            </div>
          ) : (
            <div className="heading--s container mb-12 text-neutral-400">
              {totalResultCount} résultats correspondent à votre recherche
              {` "${searchQuery}"`}
            </div>
          )}
          <div className="container flex flex-wrap gap-6">
            {results &&
              results.map((result) => {
                return (
                  <CardSearch
                    data={result}
                    className="mb-8"
                    key={result.Codename ?? result.Path ?? result.Title}
                    {...cardSearch}
                  />
                )
              })}
          </div>
          {showPagination ? (
            <footer className="flex items-center justify-center pt-12">
              {paginationList ? <Pagination list={paginationList} /> : null}
            </footer>
          ) : null}
        </Component>
      </>
    )
  }
)

export { Search }
